<template>
    <page :tabs="tabs" :busy="busy">
        <grid
            class="trigger-log-grid"
            ref="grid"
            :rowData="rowData"
            gridTheme="ag-theme-alpine-report"
            :columnDefs="columnDefs"
            :pinnedBottomRowData="pinnedBottomRowData"
            :getRowStyle="rowStyle"
            :isAutoWidth="true"
        >
            <date-range-picker
                :range-options="predefinedRanges"
                :range-months-limit="12"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <export-to-excel-button
                :href="exportExcelHref"
                :on-too-many-records="showLimitReportPeriodErrorToast"
            ></export-to-excel-button>
            <export-to-pdf-button
                :href="exportPdfHref"
                :pdfCacheKey="pdfCacheKey"
                :pageOrientation="pageOrientation"
                :on-too-many-records="showLimitReportPeriodErrorToast"
            />
            <responsive-dropdown :icon="['fas', 'file']" :items="orientationFileOption" variant="primary"
                >{{ orientationFileLabel }}
            </responsive-dropdown>
            <report-settings-modal :modalId="reportSettingsModalId" :initialValues="reportSettings" />
        </grid>
    </page>
</template>

<script>
    import format from 'date-fns/esm/format/index';
    import API from '../../../api';
    import { REPORT_TRIGGERS_LOGS_ENDPOINT } from '../../../endpoints';
    import DateRangePicker, {
        LAST_MONTH,
        LAST_WEEK,
        nameToRange,
        NEXT_MONTH,
        NEXT_WEEK,
        THIS_MONTH,
        THIS_WEEK,
        THIS_YEAR,
        TODAY,
        TOMORROW,
        YESTERDAY,
    } from '../../generic/date-range-picker/date-range-picker.vue';
    import page from '../../generic/page/page.vue';
    import Grid from '../../generic/grid/grid.vue';
    import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
    import { mapGetters } from 'vuex';
    import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';
    import ReportSettingsModal, { DATE_PERIODS_INPUT } from '../report-settings/report-settings-modal.vue';
    import MessageCellRenderer from './message-cell-renderer.vue';
    import { defineAsyncComponent } from 'vue';

    const exportToPdfButton = defineAsyncComponent(() =>
        import(
            /* webpackChunkName: "lx-export-to-pdf-button" */
            '../../generic/export-to-pdf-button/export-to-pdf-button.vue'
        )
    );

    export default {
        components: {
            page,
            Grid,
            ExportToExcelButton,
            exportToPdfButton,
            DateRangePicker,
            ResponsiveDropdown,
            ReportSettingsModal,
            messageCellRenderer: MessageCellRenderer,
        },
        name: 'TriggersLog',
        data() {
            return {
                busy: null,
                dateRange: nameToRange(THIS_MONTH),
                dateRequestFormat: 'yyyy-MM-dd',
                gridApi: {},
                landscapeOrientationFileLabel: 'Landscape',
                orientationFileLabel: 'Landscape',
                portraitOrientationFileLabel: 'Portrait',
                pageOrientation: 'landscape',
                predefinedRanges: [
                    YESTERDAY,
                    TODAY,
                    TOMORROW,
                    LAST_WEEK,
                    THIS_WEEK,
                    NEXT_WEEK,
                    LAST_MONTH,
                    THIS_MONTH,
                    NEXT_MONTH,
                    THIS_YEAR,
                ],
                rowData: [],
                rowStyle: null,
                tabs: [
                    {
                        title: 'Triggers Log Report',
                        active: true,
                    },
                ],
                pdfCacheKey: null,
                reportSettingsModalId: 'report-settings',
            };
        },
        mounted() {
            this.fetchData();
            const grid = this.$refs.grid;
            this.gridApi = grid.gridOptions.api;
            this.rowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
        },
        computed: {
            ...mapGetters('header', ['userUnicodeDateFormat']),
            columnDefs() {
                return [
                    {
                        headerName: '#',
                        field: 'number',
                        maxWidth: 50,
                        minWidth: 50,
                        sortable: false,
                        colSpan: (params) => (params.node.rowPinned ? 3 : 1),
                        valueGetter: (params) => {
                            return params.node.rowPinned
                                ? this.filteredRows && this.filteredRows.length
                                : params.node.rowIndex + 1;
                        },
                        cellClass: 'message-column',
                    },
                    {
                        field: 'created',
                        headerName: 'Date',
                        minWidth: 190,
                        maxWidth: 190,
                        type: 'dateTimeColumn',
                        cellClass: 'message-column',
                    },
                    {
                        field: 'trigger_id',
                        headerName: 'ID',
                        maxWidth: 80,
                        minWidth: 80,
                        cellClass: 'message-column',
                    },
                    {
                        field: 'has_sms',
                        headerName: 'SMS',
                        minWidth: 100,
                        valueGetter: (params) => {
                            return params.node.rowPinned ? this.calculateIsSmsRecords : params.data.has_sms ? 'X' : '';
                        },
                        cellClass: 'message-column',
                    },
                    {
                        field: 'trigger_title',
                        headerName: 'Title',
                        minWidth: 130,
                        cellClass: 'message-column',
                    },
                    {
                        field: 'meta_res',
                        headerName: 'Invoice #',
                        maxWidth: 100,
                        cellClass: 'message-column',
                    },
                    {
                        field: 'work_order.number',
                        headerName: 'Work Order #',
                        minWidth: 120,
                        cellClass: 'message-column',
                    },
                    {
                        field: 'properties',
                        headerName: 'Property/Room',
                        maxWidth: 150,
                        cellClass: 'message-column',
                    },
                    {
                        field: 'email_to',
                        headerName: 'Recipient(s)',
                        minWidth: 300,
                        cellClass: 'message-column',
                        autoHeight: true,
                        cellRenderer: ({ data }) => this.getRecipientString(data, false),
                    },
                    {
                        field: 'email_to_name',
                        headerName: 'Recipient(s) Name',
                        minWidth: 300,
                        autoHeight: true,
                        cellClass: 'message-column',
                        cellRenderer: ({ data }) => this.getRecipientString(data, true),
                    },
                    {
                        field: '',
                        headerName: 'Details',
                        minWidth: 500,
                        autoHeight: true,
                        cellRenderer: 'messageCellRenderer',
                        cellRendererParams: {
                            isMessage: true,
                        },
                        cellClass: 'message-column',
                    },
                    {
                        field: 'manual',
                        headerName: 'Manual',
                        maxWidth: 100,
                        valueGetter: (params) => {
                            if (!params.node.rowPinned) {
                                return params.data && params.data.manual ? 'Yes' : 'No';
                            }
                            return undefined;
                        },
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => (value ? 'Yes' : 'No'),
                        },
                        cellClass: 'message-column',
                    },
                    {
                        field: 'sender',
                        headerName: 'Sender',
                        maxWidth: 160,
                        valueGetter: (params) => {
                            if (!params.node.rowPinned) {
                                return (params.data && params.data.sender) || 'N/A';
                            }
                            return undefined;
                        },
                        pdfExportOptions: {
                            valueFormatter: ({ value }) => value || 'N/A',
                        },
                        cellClass: 'message-column',
                    },
                ];
            },

            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            exportExcelHref() {
                return `${REPORT_TRIGGERS_LOGS_ENDPOINT()}excel/?from_date=${this.startDateRequest}&to_date=${
                    this.endDateRequest
                }`;
            },
            exportPdfHref() {
                return `${REPORT_TRIGGERS_LOGS_ENDPOINT()}pdf/?from_date=${this.startDateRequest}&to_date=${
                    this.endDateRequest
                }`;
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            orientationFileOption() {
                return [
                    {
                        label: this.landscapeOrientationFileLabel,
                        click: () => {
                            this.pageOrientation = 'landscape';
                            this.orientationFileLabel = this.landscapeOrientationFileLabel;
                        },
                    },
                    {
                        label: this.portraitOrientationFileLabel,
                        click: () => {
                            this.pageOrientation = 'portrait';
                            this.orientationFileLabel = this.portraitOrientationFileLabel;
                        },
                    },
                ];
            },
            pinnedBottomRowData() {
                return [
                    {
                        number: this.filteredRows && this.filteredRows.length,
                        isSms: this.calculateIsSmsRecords,
                    },
                ];
            },
            calculateIsSmsRecords() {
                return this.filteredRows && this.filteredRows.filter((item) => item.data.has_sms).length;
            },
            filteredRows() {
                return this.gridApi && this.gridApi.rowModel?.rootNode?.childrenAfterFilter;
            },
            reportSettings() {
                return {
                    name: 'Triggers Log',
                    report_sysname: 'TRIGGERS_LOG',
                    settings: [
                        {
                            type: DATE_PERIODS_INPUT,
                            value: 'this_month',
                            predefinedRanges: this.predefinedRanges,
                            label: 'Period',
                            name: 'from_date',
                        },
                    ],
                };
            },
        },
        methods: {
            async fetchData() {
                try {
                    this.busy = true;
                    const { data } = await API.get(REPORT_TRIGGERS_LOGS_ENDPOINT(), {
                        params: {
                            from_date: this.startDateRequest,
                            to_date: this.endDateRequest,
                        },
                    });
                    this.rowData = data.items;
                    this.pdfCacheKey = data.pdf_cache_key;
                } catch (e) {
                    console.log(e);
                    this.errorToast({ text: 'Could not fetch logs.' });
                } finally {
                    this.busy = false;
                }
            },
            getRecipientString(data, isName) {
                const field = isName ? 'email_to_name' : 'email_to';
                const searchField = isName ? 'name' : 'contact';

                return (
                    data[field] &&
                    data[field]
                        .split(',')
                        .map((item, i) => {
                            let str = `<span>${item.trim()}</span>`;
                            if (data.details) {
                                const dispatchesLength = data.details.find(
                                    (d, i2) => d[searchField].trim() === item.trim() && i == i2
                                )?.recipient_dispatch.length;
                                str = `<span>${item.trim()}</span>${'<br/>'.repeat(dispatchesLength || 0)}`;
                            }
                            return str;
                        })
                        .join(' ')
                );
            },
            showLimitReportPeriodErrorToast() {
                this.limitReportPeriodErrorToast({
                    currentStartDate: this.dateRange.from,
                    currentEndDate: this.dateRange.to,
                });
            },
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.fetchData();
            },
        },
    };
</script>

<style lang="scss" scoped>
    .trigger-log-grid {
        :deep(.message-column) {
            & > .ag-cell-wrapper {
                top: 0;
                transform: translate(0, 0);
            }
        }
    }
</style>
